import Card from 'react-bootstrap/Card';
import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { change, Field, Form, reduxForm, SubmissionError } from 'redux-form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { renderField, renderSelect } from '../../../lib/helper/form';
import Alert from 'react-bootstrap/Alert';
import { Button } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { PUBLIC_INSURANCE_DETAILS } from '../../../lib/routing/frontend';
import { addInsurerDetails } from '../../../lib/redux/actions/insuranceDetails';
import moment from 'moment';

function UpdateInsuranceDetailsForm({ handleSubmit, invoice, dispatch, problemData, publicHealthInsurers = [], addInsurerDetails }) {
  const history = useHistory();
  const [showInvoiceField, setShowInvoiceField] = useState(true);
  const [formState, setFormState] = useState({
    formDisabled: false,
    validationMessage: '',
    authCodeRequired: false,
  });
  const [successMessage, setSuccessMessage] = useState(null);

  if (showInvoiceField && problemData) {
    setShowInvoiceField(false);
  }

  const onInsurerChange = useCallback(
    (event, newValue) => {
      const selectedInsurer = publicHealthInsurers.find((insurer) => insurer['@id'] === newValue);
      if (selectedInsurer) {
        // If the insurer is not accepted, disable the form and show a validation message
        if (!selectedInsurer.isAccepted) {
          setFormState({
            formDisabled: true,
            validationMessage:
              'Please note the doctor does not accept this insurance company, settle as a self-pay patient and we will send you a receipt so you can get reimbursed directly',
            authCodeRequired: selectedInsurer.requiresAuthCode,
          });
        } else {
          setFormState({
            formDisabled: false,
            validationMessage: '',
            authCodeRequired: selectedInsurer.requiresAuthCode,
          });
        }

        // If the selected insurer has a `membershipPrefix`, pre-fill the membership number
        if (selectedInsurer.membershipPrefix) {
          dispatch(change('UpdateInsuranceDetailsForm', 'membershipNumber', selectedInsurer.membershipPrefix));
        } else {
          // Clear the field if there's no prefix for the selected insurer
          dispatch(change('UpdateInsuranceDetailsForm', 'membershipNumber', ''));
        }
      }
    },
    [publicHealthInsurers, dispatch],
  );

  const onSubmit = (formValues) => {
    try {
      const contact = invoice?.contactRecords ? Object.values(invoice.contactRecords)[0] : false;
      if (!contact && !problemData) {
        throw new SubmissionError({ error: 'An error occurred please refresh and try again' });
      }
      //      delete formValues.invoiceNo;
      if (invoice) {
        formValues.invoice = '/invoices/' + invoice.id;
        formValues.contactRecord = '/contact_records/' + contact.id;
      }
      if (problemData) {
        formValues.problemData = problemData['@id'];

        formValues.invoiceBatch = problemData.invoiceBatch;
      }
      return addInsurerDetails(formValues).then(() => {
        history.push(PUBLIC_INSURANCE_DETAILS.SUCCESS);
      });
    } catch (e) {
      setSuccessMessage(null);
    }
  };

  const getInsurersOptions = useMemo(() => {
    return publicHealthInsurers
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((insurer) => ({
        key: insurer.id,
        value: insurer['@id'],
        text: insurer.name,
      }));
  }, [publicHealthInsurers]);

  const { formDisabled, validationMessage, authCodeRequired } = formState;

  return (
    <Card className="patient-invoice">
      <Card.Header>
        <h5>Update Insurance Details</h5>
      </Card.Header>
      <Card.Body>
        {successMessage ? (
          <Alert variant={'success'}>{successMessage}</Alert>
        ) : (
          <>
            <p>Please update your insurance details below.</p>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <fieldset disabled={formDisabled}>
                {showInvoiceField && (
                  <Row className={'text-left'}>
                    <Col>
                      <Field name="invoiceNo" label="Invoice Number" required={true} component={renderField} disabled={true} />
                    </Col>
                  </Row>
                )}
                <Row className={'text-left row'}>
                  <Col sm={12} lg={6}>
                    <Field name="patientName" label="Patient Name as held by Insurer" required={true} component={renderField} />

                    <Field name="dateOfBirth" label="Patient Date of Birth" type={'date'} component={renderField} />
                    <Field name="requestedBy" label="Your name (if different from patient)" component={renderField} />
                    <Field name="contactPhone" type="text" label="A contact phone number" component={renderField} />

                    <Field name="contactEmail" type="text" label="A contact email address" component={renderField} />
                    <Field
                      name="publicHealthInsurer"
                      label="Insurer"
                      required={true}
                      options={getInsurersOptions}
                      emptyLabel={'Please select'}
                      component={renderSelect}
                      onChange={onInsurerChange}
                    />
                    <Field name="membershipNumber" label="Membership Number" required={true} component={renderField} />
                    <Field name="authCode" label="Authorization code" component={renderField} required={authCodeRequired} />
                  </Col>
                  <Col sm={12} lg={6}>
                    <fieldset className="p-2 border border-grey rounded">
                      <Field name="address[line1]" label="Address Line 1" type="text" component={renderField} required={true} />
                      <Field name="address[line2]" label="Address Line 2" type="text" component={renderField} />
                      <Field name="address[line3]" label="Address Line 3" type="text" component={renderField} />
                      <Field name="address[city]" label="City" type="text" component={renderField} required={true} />
                      <Field name="address[county]" label="County" type="text" component={renderField} />
                      <Field name="address[postcode]" label="Postcode" type="text" component={renderField} required={true} />
                    </fieldset>
                  </Col>
                </Row>
              </fieldset>
              {!validationMessage && (
                <div className="text-center">
                  <Button type="submit" className="btn-primary text-capitalize">
                    Submit
                  </Button>
                </div>
              )}
              <div className="mt-2">
                {validationMessage && (
                  <Row>
                    <Col>
                      <Alert variant="danger">{validationMessage}</Alert>
                    </Col>
                  </Row>
                )}
              </div>
            </Form>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

const mapStateToProps = (state) => {
  const initialValues = {};

  if (state.invoices.invoice) {
    initialValues.patientName = `${state.invoices.invoice.patientFirstName} ${state.invoices.invoice.patientLastName}`;
    initialValues.dateOfBirth = moment(state.invoices.invoice.dateOfBirth).format('YYYY-MM-DD');
    initialValues.invoiceNo = state.invoices.invoice.invoiceNo;
    initialValues.invoice = '/invoices/' + state.invoices.invoice.id;
    const contact = state.invoices.invoice?.contactRecords ? Object.values(state.invoices.invoice.contactRecords)[0] : false;
    if (contact) {
      initialValues.requestedBy = contact.name;
      initialValues.contactEmail = contact.email;
      initialValues.contactPhone = contact.mobileNumber;
    }
  }
  if (state.problemDataSingle.problemData) {
    //    console.log({pd: state.problemDataSingle.problemData});
    initialValues.patientName = `${state.problemDataSingle.problemData.patientFirstName} ${state.problemDataSingle.problemData.patientLastName}`;
    initialValues.contactEmail = state.problemDataSingle.problemData.contactEmail;
    initialValues.contactPhone = state.problemDataSingle.problemData.contactPhone;
    //    console.log('ins: ' + state.problemDataSingle.problemData.insurer);
    //    initialValues.publicHealthInsurer = state.problemDataSingle.problemData.insurer;
  }
  return {
    initialValues,
    ...state.invoices,
    ...state.problemDataSingle,
    ...state.publicHealthInsurers,
  };
};

const mapDispatchToProps = {
  addInsurerDetails,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: 'UpdateInsuranceDetailsForm',
    enableReinitialize: true,
  })(UpdateInsuranceDetailsForm),
);
